import React, { useState, useEffect } from 'react';
import { Shield, Smartphone, Key, Copy, Check, AlertTriangle, Download, RefreshCw, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import api from '../utils/api';

const MFASetup = ({ onClose, onSuccess }) => {
  const [step, setStep] = useState(1); // 1: intro, 2: scan QR, 3: verify, 4: backup codes
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [setupData, setSetupData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [copiedCodes, setCopiedCodes] = useState(false);

  const startSetup = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.post('/api/auth/mfa/setup');
      setSetupData(response.data);
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start MFA setup');
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async () => {
    if (verificationCode.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }
    
    setLoading(true);
    setError('');
    try {
      const response = await api.post('/api/auth/mfa/verify-setup', {
        code: verificationCode
      });
      setBackupCodes(response.data.backup_codes || []);
      setStep(4);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'secret') {
        setCopiedSecret(true);
        setTimeout(() => setCopiedSecret(false), 2000);
      } else {
        setCopiedCodes(true);
        setTimeout(() => setCopiedCodes(false), 2000);
      }
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const downloadBackupCodes = () => {
    const content = `Athena MFA Backup Codes\n\nGenerated: ${new Date().toISOString()}\n\nStore these codes securely. Each code can only be used once.\n\n${backupCodes.map((code, i) => `${i + 1}. ${code}`).join('\n')}\n\nIf you lose access to your authenticator app, use one of these codes to sign in.`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'athena-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleComplete = () => {
    if (onSuccess) onSuccess();
    if (onClose) onClose();
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setVerificationCode(value);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md bg-white">
        <CardHeader className="relative">
          <button
            onClick={onClose}
            className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Shield className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <CardTitle>Two-Factor Authentication</CardTitle>
              <CardDescription>Secure your account with 2FA</CardDescription>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Step 1: Introduction */}
          {step === 1 && (
            <div className="space-y-4">
              <p className="text-gray-600">
                Two-factor authentication adds an extra layer of security to your account. 
                You'll need an authenticator app like:
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center gap-2">
                  <Smartphone className="w-4 h-4 text-purple-500" />
                  Google Authenticator
                </li>
                <li className="flex items-center gap-2">
                  <Smartphone className="w-4 h-4 text-purple-500" />
                  Microsoft Authenticator
                </li>
                <li className="flex items-center gap-2">
                  <Smartphone className="w-4 h-4 text-purple-500" />
                  Authy
                </li>
              </ul>
              <Button 
                onClick={startSetup} 
                disabled={loading}
                className="w-full bg-purple-600 hover:bg-purple-700"
              >
                {loading ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Setting up...</>
                ) : (
                  'Get Started'
                )}
              </Button>
            </div>
          )}

          {/* Step 2: QR Code */}
          {step === 2 && setupData && (
            <div className="space-y-4">
              <p className="text-gray-600 text-sm">
                Scan this QR code with your authenticator app:
              </p>
              <div className="flex justify-center p-4 bg-white rounded-lg border">
                <img 
                  src={setupData.qr_code} 
                  alt="MFA QR Code" 
                  className="w-48 h-48"
                />
              </div>
              <div className="space-y-2">
                <p className="text-xs text-gray-500">Can't scan? Enter this code manually:</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 p-2 bg-gray-100 rounded text-xs font-mono break-all">
                    {setupData.secret}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(setupData.secret, 'secret')}
                  >
                    {copiedSecret ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              <Button 
                onClick={() => setStep(3)}
                className="w-full bg-purple-600 hover:bg-purple-700"
              >
                Continue
              </Button>
            </div>
          )}

          {/* Step 3: Verify Code */}
          {step === 3 && (
            <div className="space-y-4">
              <p className="text-gray-600 text-sm">
                Enter the 6-digit code from your authenticator app:
              </p>
              <div className="flex justify-center">
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={verificationCode}
                  onChange={handleCodeChange}
                  placeholder="000000"
                  className="text-center text-2xl tracking-widest w-40 font-mono"
                />
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline"
                  onClick={() => setStep(2)}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button 
                  onClick={verifyCode}
                  disabled={loading || verificationCode.length !== 6}
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                >
                  {loading ? (
                    <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Verifying...</>
                  ) : (
                    'Verify'
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Step 4: Backup Codes */}
          {step === 4 && (
            <div className="space-y-4">
              <Alert className="bg-green-50 border-green-200">
                <Check className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  Two-factor authentication is now enabled!
                </AlertDescription>
              </Alert>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Key className="w-4 h-4 text-amber-500" />
                  <h4 className="font-medium">Backup Codes</h4>
                </div>
                <p className="text-xs text-gray-500">
                  Save these backup codes securely. Each code can only be used once.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2 p-3 bg-gray-50 rounded-lg">
                {backupCodes.map((code, index) => (
                  <code key={index} className="text-xs font-mono text-center py-1 bg-white rounded">
                    {code}
                  </code>
                ))}
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => copyToClipboard(backupCodes.join('\n'), 'codes')}
                  className="flex-1"
                >
                  {copiedCodes ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                  Copy
                </Button>
                <Button
                  variant="outline"
                  onClick={downloadBackupCodes}
                  className="flex-1"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              </div>

              <Alert className="bg-amber-50 border-amber-200">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800 text-xs">
                  These codes won't be shown again. Store them somewhere safe!
                </AlertDescription>
              </Alert>

              <Button 
                onClick={handleComplete}
                className="w-full bg-purple-600 hover:bg-purple-700"
              >
                Done
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MFASetup;
